import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Input, Label, Row, Col, Progress } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

// Alta masiva de modelos desde un Excel (layout). El layout se descarga aqui
// mismo con el boton "Descargar layout".
//
// Reglas: CODIGO, NOMBRE, LINEA, MARCA, FAMILIA y PROVEEDOR son obligatorios
// (esos catalogos no tienen "NA"); un codigo que ya existe en el sistema (o
// repetido dentro del archivo) NO se da de alta. COLECCION y COMPOSICION vacias
// = NA; EMPRESA vacia = AMERICANFLY 2652. CODIGO SAT: 8 digitos; vacio = NA
// (se avisa porque sin codigo SAT el modelo no se puede facturar).
// Cada modelo nuevo nace con su inventario en CEDIS en 0, igual que el alta normal.
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const env = (k) => process.env[k];
const txt = (v) => String(v === undefined || v === null ? "" : v).trim();
const nu = (v) => Number(String(v ?? "").toString().replace(/[$,\s]/g, "")) || 0;
const norm = (v) => txt(v).toUpperCase().replace(/\s+/g, " ");
const EMPRESA_DEFAULT = "AMERICANFLY 2652";

// Columnas del layout: [encabezado, obligatorio?, ejemplo]
const COLUMNAS = [
  ["CODIGO", true, "EJEMPLO-BORRAR"],
  ["NOMBRE", true, "CHAMARRA CABALLERO BASICA"],
  ["DESCRIPCION", false, "CHAMARRA CON CIERRE"],
  ["LINEA", true, ""],
  ["MARCA", true, ""],
  ["FAMILIA", true, ""],
  ["PROVEEDOR", true, ""],
  ["COLECCION", false, "NA"],
  ["COMPOSICION", false, "NA"],
  ["EMPRESA", false, EMPRESA_DEFAULT],
  ["ANO", false, "2026"],
  ["PIEZAS POR CAJA", false, 24],
  ["COSTO USD", false, 0],
  ["COSTO MX", false, 0],
  ["VENTA", false, 0],
  ["PRECIO TIENDA", false, 0],
  ["EAN", false, "NA"],
  ["CODIGO SAT", false, "53101602"],
  ["OBSERVACIONES", false, "NA"],
];

function ImportarArticulosModal({ abierto, cerrar, onTerminado, userId }) {
  const [cats, setCats] = useState({});
  const [filas, setFilas] = useState([]);
  const [archivo, setArchivo] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [avance, setAvance] = useState(0);

  useEffect(() => {
    if (!abierto) return;
    setFilas([]); setArchivo(""); setAvance(0);
    setCats({});
    const pide = (url) => axios.get(url, { headers: headers() }).then((r) => r.data || []).catch(() => []);
    // Los codigos existentes (para duplicados) van aparte y por la ruta ligera:
    // la lista completa de articulos pesa ~13 MB y tardaba en abrir el layout.
    pide(`${env("REACT_APP_URL_ARTICULOS")}Select`).then((articulos) =>
      setCats((c) => ({ ...c, articulos }))
    );
    Promise.all([
      pide(env("REACT_APP_URL_LINEAS")),
      pide(env("REACT_APP_URL_MARCAS")),
      pide(env("REACT_APP_URL_FAMILIAS")),
      pide(env("REACT_APP_URL_COLECCIONES")),
      pide(env("REACT_APP_URL_COMPOSICIONES")),
      pide(env("REACT_APP_URL_PROVEEDORES")),
      pide(env("REACT_APP_URL_EMPRESAS")),
    ]).then(([lineas, marcas, familias, colecciones, composiciones, proveedores, empresas]) =>
      setCats((c) => ({ ...c, lineas, marcas, familias, colecciones, composiciones, proveedores, empresas }))
    );
  }, [abierto]);

  // Busca en un catalogo por nombre (ignora mayusculas y espacios de mas)
  const busca = (lista, valor, campo = "name") => {
    const t = norm(valor);
    if (!t) return null;
    return (lista || []).find((x) => norm(x[campo]) === t) || null;
  };
  const activos = (lista) => (lista || []).filter((x) => x.is_active !== "No");

  function descargaLayout() {
    const wb = XLSX.utils.book_new();
    // Hoja 1: encabezados + un renglon de ejemplo
    const hoja = XLSX.utils.aoa_to_sheet([
      COLUMNAS.map(([c, ob]) => (ob ? `${c} *` : c)),
      COLUMNAS.map(([, , ej]) => ej),
    ]);
    hoja["!cols"] = COLUMNAS.map(([c]) => ({ wch: Math.max(14, c.length + 4) }));
    XLSX.utils.book_append_sheet(wb, hoja, "Articulos");
    // Hoja 2: como llenarlo + catalogos vivos
    const nombres = (lista, campo = "name") =>
      activos(lista).map((x) => txt(x[campo])).filter(Boolean).sort().join(" | ");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ["COMO LLENAR ESTE LAYOUT"],
      [],
      ["1", "Captura un modelo por renglon, empezando en el renglon 3. El renglon 2 es un ejemplo: se puede borrar, y aunque se deje no se da de alta."],
      ["2", "Las columnas con * son obligatorias: CODIGO, NOMBRE, LINEA, MARCA, FAMILIA y PROVEEDOR."],
      ["3", "El CODIGO no se puede repetir. Si ya existe en el sistema o se repite en el archivo, ese renglon no se da de alta."],
      ["4", "LINEA, MARCA, FAMILIA, PROVEEDOR, COLECCION, COMPOSICION y EMPRESA se buscan por nombre: copialos tal cual de la lista de abajo."],
      ["5", `COLECCION y COMPOSICION vacias = NA. EMPRESA vacia = ${EMPRESA_DEFAULT}.`],
      ["6", "CODIGO SAT: clave de producto del SAT de 8 digitos (ejemplo 53101602). Sin codigo SAT el modelo no se puede facturar."],
      ["7", "Los importes y PIEZAS POR CAJA van en numero, sin signo de pesos; si se dejan vacios quedan en 0."],
      ["8", "Los textos que se dejen vacios (descripcion, ano, EAN, observaciones) se guardan como NA."],
      ["9", "No cambies los nombres de las columnas."],
      [],
      ["CATALOGOS DISPONIBLES (copia el nombre tal cual)"],
      ["Lineas", nombres(cats.lineas)],
      ["Marcas", nombres(cats.marcas)],
      ["Familias", nombres(cats.familias)],
      ["Proveedores", nombres(cats.proveedores, "nombre_comercial")],
      ["Colecciones", nombres(cats.colecciones)],
      ["Composiciones", nombres(cats.composiciones)],
      ["Empresas", nombres(cats.empresas)],
    ]), "Instrucciones");
    XLSX.writeFile(wb, "LayoutAltaArticulos.xlsx");
  }

  function leeArchivo(file) {
    if (!file) return;
    setArchivo(file.name); setAvance(0);
    const lector = new FileReader();
    lector.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const hoja = wb.Sheets[wb.SheetNames[0]];
        const crudas = XLSX.utils.sheet_to_json(hoja, { defval: "" });
        setFilas(revisa(crudas));
      } catch (err) {
        Swal.fire({ icon: "error", title: "No se pudo leer el archivo", text: "Usa el layout que se descarga de este mismo boton." });
      }
    };
    lector.readAsArrayBuffer(file);
  }

  // Valida cada renglon y deja listo el cuerpo que se va a mandar
  function revisa(crudas) {
    const dame = (f, col) => {
      const k = Object.keys(f).find((x) => norm(x).replace(/\*/g, "").trim() === col);
      return k ? txt(f[k]) : "";
    };
    const existentes = new Set((cats.articulos || []).map((a) => norm(a.codigo)));
    const vistos = new Set();
    return crudas.map((f, i) => {
      const codigo = dame(f, "CODIGO");
      const nombre = dame(f, "NOMBRE");
      const fila = { renglon: i + 2, codigo, nombre };
      if (!codigo && !nombre) return { ...fila, estado: "vacio" };
      // El renglon de ejemplo del layout se ignora aunque no lo borren
      if (norm(codigo).startsWith("EJEMPLO")) return { ...fila, estado: "vacio" };

      const faltan = ["CODIGO", "NOMBRE", "LINEA", "MARCA", "FAMILIA", "PROVEEDOR"].filter((c) => !dame(f, c));
      if (faltan.length) return { ...fila, estado: "error", detalle: `Falta ${faltan.join(", ")}` };
      if (existentes.has(norm(codigo))) return { ...fila, estado: "duplicado", detalle: "Ya existe en el sistema" };
      if (vistos.has(norm(codigo))) return { ...fila, estado: "duplicado", detalle: "Repetido en el archivo" };

      // Catalogos obligatorios: si no existen, el renglon no pasa
      const noExisten = [];
      const obligatorio = (col, lista, campo = "name") => {
        const x = busca(lista, dame(f, col), campo);
        if (!x) noExisten.push(`${col} "${dame(f, col)}"`);
        return x && x._id;
      };
      const lineas = obligatorio("LINEA", cats.lineas);
      const marcas = obligatorio("MARCA", cats.marcas);
      const familias = obligatorio("FAMILIA", cats.familias);
      const proveedores = obligatorio("PROVEEDOR", cats.proveedores, "nombre_comercial");
      const empresaTxt = dame(f, "EMPRESA") || EMPRESA_DEFAULT;
      const empresa = busca(cats.empresas, empresaTxt);
      if (!empresa) noExisten.push(`EMPRESA "${empresaTxt}"`);
      if (noExisten.length) return { ...fila, estado: "error", detalle: `No existe ${noExisten.join(", ")}` };

      // Opcionales con NA
      const avisos = [];
      const conNA = (col, lista) => {
        const valor = dame(f, col);
        const x = busca(lista, valor);
        if (valor && !x) avisos.push(`${col} no existe: queda en NA`);
        return (x || busca(lista, "NA"))?._id;
      };
      const colecciones = conNA("COLECCION", cats.colecciones);
      const composiciones = conNA("COMPOSICION", cats.composiciones);
      if (!colecciones || !composiciones) return { ...fila, estado: "error", detalle: "Falta el catalogo NA de coleccion o composicion" };

      // Codigo SAT: 8 digitos; vacio = NA con aviso
      const satTxt = dame(f, "CODIGO SAT").replace(/\s/g, "");
      if (satTxt && !/^\d{8}$/.test(satTxt)) {
        return { ...fila, estado: "error", detalle: `CODIGO SAT "${satTxt}" invalido: deben ser 8 digitos` };
      }
      if (!satTxt) avisos.push("sin CODIGO SAT (no se podra facturar)");

      vistos.add(norm(codigo));
      const oNAtxt = (col) => dame(f, col) || "NA";
      const cuerpo = {
        codigo, nombre,
        descripcion: oNAtxt("DESCRIPCION"),
        piezasPorCaja: nu(dame(f, "PIEZAS POR CAJA")),
        costoUSD: nu(dame(f, "COSTO USD")),
        costoMX: nu(dame(f, "COSTO MX")),
        venta: nu(dame(f, "VENTA")),
        precioTienda: nu(dame(f, "PRECIO TIENDA")),
        observaciones: oNAtxt("OBSERVACIONES"),
        ano: oNAtxt("ANO"),
        EAN: oNAtxt("EAN"),
        codigoSAT: satTxt || "NA",
        codigoUnidad: "NA",
        lineas, marcas, familias, colecciones, composiciones, proveedores,
        empresas: empresa._id,
        pesoCaja: 0, tamanoCaja: "0", vendedores: "Si", visibleInventario: "Si",
      };
      return { ...fila, estado: "nuevo", cuerpo, detalle: avisos.join(" · ") };
    }).filter((f) => f.estado !== "vacio");
  }

  const nuevos = filas.filter((f) => f.estado === "nuevo");
  const duplicados = filas.filter((f) => f.estado === "duplicado");
  const errores = filas.filter((f) => f.estado === "error");

  async function guardar() {
    if (!nuevos.length) return;
    const ok = await Swal.fire({
      icon: "question", title: `Se daran de alta ${nuevos.length} modelo(s)`,
      html: duplicados.length || errores.length
        ? `<b style="color:#b45309">${duplicados.length} duplicado(s) y ${errores.length} con error se omiten.</b>`
        : "",
      showCancelButton: true, confirmButtonText: "Si, dar de alta", cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    setGuardando(true);
    const fallaron = [];
    for (let i = 0; i < nuevos.length; i++) {
      try {
        const r = await axios.post(env("REACT_APP_URL_ARTICULOS"), nuevos[i].cuerpo, { headers: headers() });
        // Su renglon de inventario en CEDIS, igual que el alta de un modelo
        await axios.post(
          env("REACT_APP_URL_INVENTARIOS"),
          { articulos: r.data._id, areas: env("REACT_APP_AREAS_CEDIS") },
          { headers: headers() }
        );
      } catch (error) {
        fallaron.push(`${nuevos[i].codigo}: ${error.response?.data?.message || error.message}`);
      }
      setAvance(Math.round(((i + 1) / nuevos.length) * 100));
    }
    try {
      await axios.post(
        env("REACT_APP_URL_LOGS"),
        { tipo: "Alta masiva modelos", detalle: `${nuevos.length - fallaron.length} modelos desde layout`, user: userId },
        { headers: headers() }
      );
    } catch (e) { /* el log no detiene el alta */ }
    setGuardando(false);
    await Swal.fire({
      icon: fallaron.length ? "warning" : "success",
      title: `${nuevos.length - fallaron.length} modelo(s) dados de alta`,
      html: fallaron.length ? `No se pudieron dar de alta:<br><small>${fallaron.slice(0, 10).join("<br>")}</small>` : "",
    });
    cerrar();
    if (onTerminado) onTerminado();
  }

  const color = { nuevo: "#15803d", duplicado: "#b45309", error: "#dc2626" };
  return (
    <Modal size="lg" isOpen={abierto} toggle={cerrar}>
      <ModalHeader toggle={cerrar}>Alta de modelos desde layout</ModalHeader>
      <ModalBody>
        <Row>
          <Col md={5}>
            <Label><b>1. Layout</b></Label>
            <div>
              <Button size="sm" color="info" onClick={descargaLayout} disabled={!cats.lineas}>
                <i className="far fa-file-excel" /> Descargar layout
              </Button>
            </div>
            <small style={{ color: "#64748b" }}>
              {cats.lineas ? "Trae las instrucciones y los catálogos para llenarlo." : "Cargando catálogos..."}
            </small>
          </Col>
          <Col md={7}>
            <Label><b>2. Sube el layout lleno</b></Label>
            <Input type="file" accept=".xlsx,.xls" disabled={!cats.articulos || !cats.lineas} onChange={(e) => leeArchivo(e.target.files[0])} />
            {!cats.articulos || !cats.lineas ? (
              <small style={{ color: "#b45309" }}>Cargando códigos existentes...</small>
            ) : archivo ? (
              <small style={{ color: "#64748b" }}>{archivo}</small>
            ) : null}
          </Col>
        </Row>
        <br />
        {filas.length ? (
          <>
            <div style={{ display: "flex", gap: 14, marginBottom: 6, fontSize: 13 }}>
              <span style={{ color: color.nuevo, fontWeight: 700 }}>{nuevos.length} por dar de alta</span>
              <span style={{ color: color.duplicado, fontWeight: 700 }}>{duplicados.length} duplicados</span>
              <span style={{ color: color.error, fontWeight: 700 }}>{errores.length} con error</span>
            </div>
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              <Table size="sm" bordered>
                <thead>
                  <tr><th>Renglón</th><th>Código</th><th>Nombre</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {filas.map((f) => (
                    <tr key={f.renglon}>
                      <td>{f.renglon}</td>
                      <td>{f.codigo}</td>
                      <td>{f.nombre}</td>
                      <td style={{ color: color[f.estado], fontWeight: 600 }}>
                        {f.estado === "nuevo" ? (f.detalle ? `Se dará de alta · ${f.detalle}` : "Se dará de alta") : f.detalle}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        ) : (
          <p style={{ color: "#9ca3af" }}>Sube el layout para ver qué se va a dar de alta.</p>
        )}
        {guardando ? <Progress animated value={avance}>{avance}%</Progress> : null}
      </ModalBody>
      <ModalFooter>
        <Button color="success" onClick={guardar} disabled={guardando || !nuevos.length}>
          {guardando ? "Dando de alta..." : `Dar de alta ${nuevos.length} modelo(s)`}
        </Button>
        <Button color="secondary" onClick={cerrar} disabled={guardando}>Cerrar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default ImportarArticulosModal;
